import { useMediaQuery } from '@mui/material';
import { cn } from '@natter-co/design-system';

import { GlowBreathe } from './PulseGlow.styled';

import type { Props } from './types';

/**
 * The Natalie blue edge glow from natalie-templates: the border moves to `--border-accent-muted` and an inset
 * accent shadow fades in (300ms ease-out), with a slow 2.4s breathe while `on`. The glow sits under the content,
 * so text near the edge is never tinted. A steady glow under reduced motion.
 *
 * DS gap: the design system has no glow surface. Ported from `natalie/PulseGlow.tsx` in the post-event-feedback
 * prototype.
 */
export const PulseGlow = ({ on, className, contentClassName, children, ...rest }: Props) => {
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true });

  return (
    <div
      {...rest}
      data-glow={on || undefined}
      className={cn(
        'group/glow relative border border-[var(--border-primary)] transition-[border-color] duration-300 ease-[ease-out] data-[glow]:border-[var(--border-accent-muted)] motion-reduce:transition-none',
        className
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-300 ease-[ease-out] group-data-[glow]/glow:opacity-100 motion-reduce:transition-none"
      >
        <GlowBreathe data-testid="pulse-glow-breathe" data-breathe={(on && !reduced) || undefined} />
      </span>
      <div className={cn('relative', contentClassName)}>{children}</div>
    </div>
  );
};
